import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { trackVisit } from './lib/analytics';

// ScrollToTop on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Visitor analytics tracker: fires on every route change (filtered to public pages
// inside trackVisit). Mounted once, returns nothing.
const VisitTracker = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    trackVisit(pathname);
  }, [pathname]);
  return null;
};
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { I18nProvider } from './contexts/I18nContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { TerritoryProvider } from './contexts/TerritoryContext';
import { SiteLogoProvider } from './contexts/SiteLogoContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Components
import { Navbar } from './components/Navbar';
import { CookieBanner } from './components/CookieBanner';
import { Starfield } from './components/Starfield';

// Pages
import { Landing } from './pages/Landing';
import { Explore } from './pages/Explore';
import { EventDetail } from './pages/EventDetail';
import { MapView } from './pages/MapView';
import { ProviderProfile } from './pages/ProviderProfile';
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { ForProviders } from './pages/ForProviders';
import { TicketsPrintPage } from './pages/provider/TicketsPrintPage';
import { ProviderDashboard } from './pages/provider/Dashboard';
import { AdminDashboard } from './pages/admin/Dashboard';
import ReferrerDashboard from './pages/referrer/Dashboard';
import { Terms } from './pages/legal/Terms';
import { Privacy } from './pages/legal/Privacy';
import { Cookies } from './pages/legal/Cookies';
import MePage from './pages/MePage';
import PublicProfilePage from './pages/PublicProfilePage';
import InvitePage from './pages/InvitePage';
import JoinListPage from './pages/JoinListPage';
import { LegalNotice } from './pages/legal/LegalNotice';
import Contact from './pages/Contact';
import SeoLanding from './pages/SeoLanding';

// Protected Route Components
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAF9]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00CC66]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole === 'admin' && user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  if (requiredRole === 'provider' && user.role !== 'provider' && user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  if (requiredRole === 'referrer' && user.role !== 'referrer') {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Layout with Navbar
const MainLayout = ({ children, showNavbar = true }) => {
  return (
    <>
      {showNavbar && <Navbar />}
      {children}
    </>
  );
};

function AppRoutes() {
  // Load favicon from site settings on mount
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/favicon`)
      .then(res => res.json())
      .then(data => {
        if (data.url) {
          const fullUrl = data.url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${data.url}` : data.url;
          let link = document.querySelector("link[rel~='icon']");
          if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
          }
          link.href = fullUrl;
        }
      })
      .catch(() => {});
  }, []);

  return (
    <>
    <Starfield />
    <ScrollToTop />
    <VisitTracker />
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<MainLayout><Landing /></MainLayout>} />
      <Route path="/explore" element={<MainLayout><Explore /></MainLayout>} />
      <Route path="/map" element={<MainLayout><MapView /></MainLayout>} />
      <Route path="/event/:id" element={<MainLayout><EventDetail /></MainLayout>} />
      <Route path="/artist/:id" element={<MainLayout><ProviderProfile /></MainLayout>} />

      {/* SEO landing pages (commune / category) */}
      <Route path="/panoramas/:comuna" element={<MainLayout><SeoLanding /></MainLayout>} />
      <Route path="/panoramas/:comuna/:categoria" element={<MainLayout><SeoLanding /></MainLayout>} />
      <Route path="/categoria/:categoria" element={<MainLayout><SeoLanding /></MainLayout>} />
      
      {/* Auth routes - no navbar */}
      <Route path="/login" element={<MainLayout showNavbar={false}><Login /></MainLayout>} />
      <Route path="/register" element={<MainLayout showNavbar={false}><Register /></MainLayout>} />

      {/* Commercial landing for provider signup (no navbar, self-contained) */}
      <Route path="/para-proveedores" element={<MainLayout showNavbar={false}><ForProviders /></MainLayout>} />

      {/* Legal pages (self-contained dark layout, no navbar) */}
      <Route path="/terminos" element={<MainLayout showNavbar={false}><Terms /></MainLayout>} />
      <Route path="/privacidad" element={<MainLayout showNavbar={false}><Privacy /></MainLayout>} />
      <Route path="/cookies" element={<MainLayout showNavbar={false}><Cookies /></MainLayout>} />
      <Route path="/aviso-legal" element={<MainLayout showNavbar={false}><LegalNotice /></MainLayout>} />
      <Route path="/contacto" element={<MainLayout showNavbar={true}><Contact /></MainLayout>} />

      {/* Printable tickets list (provider only, no navbar) */}
      <Route path="/provider/tickets-print/:eventId" element={<ProtectedRoute requiredRole="provider"><TicketsPrintPage /></ProtectedRoute>} />
      
      {/* Provider routes */}
      <Route
        path="/provider"
        element={
          <ProtectedRoute requiredRole="provider">
            <MainLayout><ProviderDashboard /></MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Admin routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredRole="admin">
            <MainLayout><AdminDashboard /></MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Referrer routes */}
      <Route
        path="/referrer"
        element={
          <ProtectedRoute requiredRole="referrer">
            <MainLayout showNavbar={false}><ReferrerDashboard /></MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* User engagement: favorites, plans, lists, recommendations */}
      <Route path="/me" element={<MainLayout><MePage /></MainLayout>} />
      <Route path="/u/:username" element={<MainLayout><PublicProfilePage /></MainLayout>} />
      <Route path="/invite/:token" element={<MainLayout><InvitePage /></MainLayout>} />
      <Route path="/lists/join/:shareToken" element={<MainLayout><JoinListPage /></MainLayout>} />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    <CookieBanner />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <AuthProvider>
        <I18nProvider>
          <CurrencyProvider>
            <TerritoryProvider>
              <SiteLogoProvider>
                <ThemeProvider>
                  <AppRoutes />
                  <Toaster position="top-right" />
                </ThemeProvider>
              </SiteLogoProvider>
            </TerritoryProvider>
          </CurrencyProvider>
        </I18nProvider>
      </AuthProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
